// MultiSectionCheckboxDropdown — reusable multi-select checkbox popover. Renders
// N uniform grouped sections of checkbox options inside a Dropdown; every section
// looks the same (heading + checkbox rows). The selected Sets and the option data
// are owned by the caller (presentational); only the open state lives here. Backs
// both toolbar filters and the table column-visibility toggle. Checkbox rows reuse
// the shared .app-checkbox visuals.

import { useMemo, useState } from 'react';
import Checkbox from './Checkbox';
import Dropdown from './Dropdown';
import Icon from './Icon';

export interface MultiSectionCheckboxOption {
  id: string;
  label: string;
  /** Render the row disabled (e.g. a locked/always-on option). */
  disabled?: boolean;
}

export interface MultiSectionCheckboxSection {
  key: string;
  label: string;
  options: MultiSectionCheckboxOption[];
}

export interface MultiSectionCheckboxDropdownProps {
  /** Trigger label, also the accessible name. */
  label: string;
  sections: MultiSectionCheckboxSection[];
  /** sectionKey -> set of selected option ids. */
  selected: Record<string, Set<string>>;
  onToggle: (sectionKey: string, id: string) => void;
  /** Per-section clear — when provided, a Clear button shows beside a section's
   *  label while that section has a selection, clearing only that section. */
  onClearSection?: (sectionKey: string) => void;
  clearLabel?: string;
  emptyText?: string;
  /** Trigger icon. Default 'list-filter'. */
  icon?: string;
  /** Show the selected-count badge on the trigger. Default false. */
  showCount?: boolean;
  /** Static, non-brand trigger (no brand hover/open tint) — e.g. a column
   *  toggle where the brand "active" affordance would be misleading. */
  plainTrigger?: boolean;
  /** Render the panel in a body portal to escape a clipping ancestor. */
  portal?: boolean;
  /** Panel edge to align with the trigger. Default 'start' (toolbar left). */
  align?: 'start' | 'end';
  width?: number;
  /** Trigger size. Default 'md' (toolbar height); 'sm' is the compact variant
   *  for dense contexts like table cells. Opt-in — existing call sites keep
   *  their look. */
  size?: 'md' | 'sm';
}

const MultiSectionCheckboxDropdown = ({
  label,
  sections,
  selected,
  onToggle,
  onClearSection,
  clearLabel,
  emptyText,
  icon = 'list-filter',
  showCount = false,
  plainTrigger = false,
  portal = false,
  align = 'start',
  width = 300,
  size = 'md',
}: MultiSectionCheckboxDropdownProps) => {
  const [open, setOpen] = useState(false);

  const count = useMemo(() => Object.values(selected).reduce((n, s) => n + s.size, 0), [selected]);
  const triggerClass = [
    'checkbox-dropdown__trigger',
    plainTrigger && 'checkbox-dropdown__trigger--plain',
    size === 'sm' && 'checkbox-dropdown__trigger--sm',
    !plainTrigger && (count > 0 || open) && 'is-open',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align={align}
      width={width}
      portal={portal}
      className='checkbox-dropdown'
      trigger={
        <button
          type='button'
          className={triggerClass}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name={icon} size={16} />
          {label}
          {showCount && count > 0 && <span className='checkbox-dropdown__count'>{count}</span>}
          <Icon
            name={open ? 'chevron-up' : 'chevron-down'}
            size={16}
            className='checkbox-dropdown__caret'
          />
        </button>
      }
    >
      <div className='checkbox-dropdown__sections'>
        {sections.map((section) => {
          const picked = selected[section.key] ?? new Set<string>();
          return (
            <div key={section.key} className='checkbox-dropdown__section'>
              <div className='checkbox-dropdown__section-head'>
                <span className='checkbox-dropdown__label'>{section.label}</span>
                {onClearSection && picked.size > 0 && (
                  <button
                    type='button'
                    className='checkbox-dropdown__section-clear'
                    onClick={() => onClearSection(section.key)}
                  >
                    {clearLabel}
                  </button>
                )}
              </div>
              <div className='checkbox-dropdown__list'>
                {section.options.map((o) => (
                  <Checkbox
                    key={o.id}
                    className='checkbox-dropdown__opt'
                    label={o.label}
                    checked={picked.has(o.id)}
                    disabled={o.disabled}
                    onChange={() => onToggle(section.key, o.id)}
                    size='sm'
                  />
                ))}
                {section.options.length === 0 && (
                  <span className='checkbox-dropdown__empty'>{emptyText}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Dropdown>
  );
};

export default MultiSectionCheckboxDropdown;
